import { notification } from 'antd';
import type { PageableResponse } from '../types';
import type {
  CreateTeacherWorkloadBody,
  TeacherWorkload,
  TeacherWorkloadsRequest,
  UpdateTeacherWorkloadBody,
} from './types';
import { api } from '../api';

export const teacherWorkloadsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getTeacherWorkloads: build.query<PageableResponse<TeacherWorkload>, TeacherWorkloadsRequest>({
      query: (params = {}) => ({
        url: '/teacher-workloads',
        params: {
          ...params,
          subjectIds: params.subjectIds?.join(',') || undefined,
          educationClassIds: params.educationClassIds?.join(',') || undefined,
          sort: params.sort?.join(',') || undefined,
        },
      }),
      keepUnusedDataFor: 30,
      providesTags: (result) => (result ? [{ type: 'TeacherWorkloads' as const, id: 'LIST' }] : []),
    }),
    getTeacherWorkloadById: build.query<TeacherWorkload, number>({
      query: (id) => ({
        url: `/teacher-workloads/${id}`,
      }),
      keepUnusedDataFor: 0,
      providesTags: (result) =>
        result ? [{ type: 'TeacherWorkloads' as const, id: result.id }] : [],
    }),
    createTeacherWorkload: build.mutation<TeacherWorkload, CreateTeacherWorkloadBody>({
      query: (body) => ({
        url: '/teacher-workloads',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'TeacherWorkloads' as const, id: 'LIST' }],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({ message: 'Нагрузка учителя создана' });
        } catch {
          notification.error({ message: 'Не удалось создать нагрузку учителя' });
        }
      },
    }),
    updateTeacherWorkload: build.mutation<TeacherWorkload, UpdateTeacherWorkloadBody>({
      query: ({ id, ...body }) => ({
        url: `/teacher-workloads/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'TeacherWorkloads' as const, id: 'LIST' }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            teacherWorkloadsApi.util.updateQueryData('getTeacherWorkloadById', arg.id, () => data),
          );
          notification.success({ message: 'Нагрузка учителя обновлена' });
        } catch {
          notification.error({ message: 'Не удалось обновить нагрузку учителя' });
        }
      },
    }),
    deleteTeacherWorkload: build.mutation<void, number>({
      query: (id) => ({
        url: `/teacher-workloads/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'TeacherWorkloads' as const, id: 'LIST' },
        { type: 'TeacherWorkloads' as const, id },
      ],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          notification.success({ message: 'Нагрузка учителя удалена' });
        } catch {
          notification.error({ message: 'Не удалось удалить нагрузку учителя' });
        }
      },
    }),
  }),
});

export const {
  useGetTeacherWorkloadsQuery,
  useLazyGetTeacherWorkloadsQuery,
  useGetTeacherWorkloadByIdQuery,
  useLazyGetTeacherWorkloadByIdQuery,
  useCreateTeacherWorkloadMutation,
  useUpdateTeacherWorkloadMutation,
  useDeleteTeacherWorkloadMutation,
} = teacherWorkloadsApi;
